import { useMemo } from "react"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"
import clsx from "clsx"
import { useTranslations } from "next-intl"

import { CardPrices, createDiscountDisplayOptions, determineCardPrices } from "@/lib/price-utils"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"

const SHOW_ADDITIONAL_BADGES = true

function determineSpecialFlags(product: ReducedProductData) {
  let isOwnBrand = false
  let isNew = false
  let isFastAndCheap = false
  let isDoNotPromoteAll = false

  if (product) {
    isOwnBrand = product.brand === "UDOBÄR"
    isNew = product.isNew ?? false
    isDoNotPromoteAll = product.isDoNotPromoteAll ?? false

    //todo - correct implementation
    isFastAndCheap = product.category === "Schnell & Preiswert" || (product?.isTongeren ?? false)
  }
  return { isOwnBrand, isNew, isFastAndCheap, isDoNotPromoteAll }
}

function CardDiscountBadge(props: { className?: string | undefined; variant: string | undefined; label: string }) {
  return (
    <div
      className={cn(
        "flex h-full items-center justify-center rounded-sm bg-primary text-primary-foreground",
        props.className
      )}
    >
      <p
        className={cn("p-1.5 text-xs font-bold", {
          "p-1 text-xs": props.variant === "landscape",
        })}
      >
        {props.label && props.label !== "" && <>{props.label}</>}
      </p>
    </div>
  )
}

export default function ProductCardDiscount(props: {
  isoLocale: string
  product: ReducedProductData
  prices: ProductPriceContainerCtnrROV1 | undefined
  variant?: string
  isB2BCustomer: boolean
  generalDiscount?: number
  customerCategoryDiscount?: number
  legacyPrices?: boolean
  quantity?: number
  _cardPrices?: CardPrices
  className?: clsx.ClassValue | string
}) {
  const t = useTranslations("storefront")

  const effectiveCardPrices = useMemo(() => {
    if (props._cardPrices !== undefined) {
      return props._cardPrices
    } else {
      if (props.legacyPrices) {
        // Hinweis: originalProductId (Master-ID) durchreichen, damit isSinglePrice korrekt
        // am Master geprüft wird und das "Ab"-Label bei Varianten wieder erscheint.
        // Bei Legacy-Preisen ist die EcoTax bereits enthalten (daher 0), hat aber
        // keinen Einfluss auf die Ermittlung von isSinglePrice.
        return determineCardPrices(
          props.isoLocale,
          props.isB2BCustomer,
          props.prices,
          0,
          props.quantity,
          props.product?.id,
          createDiscountDisplayOptions(props.product, props.generalDiscount, props.customerCategoryDiscount)
        )
      } else {
        // Gleiches Prinzip wie oben: Master-ID mitgeben, damit bei cheapestVariant-Preisen
        // die Single-/Ab-Logik am Master hängt und nicht an der Varianten-SKU.
        return determineCardPrices(
          props.isoLocale,
          props.isB2BCustomer,
          props.prices,
          props.product?.ecoTax,
          props.quantity,
          props.product?.id,
          createDiscountDisplayOptions(props.product, props.generalDiscount, props.customerCategoryDiscount)
        )
      }
    }
  }, [
    props._cardPrices,
    props.legacyPrices,
    props.isoLocale,
    props.isB2BCustomer,
    props.prices,
    props.quantity,
    props.generalDiscount,
    props.customerCategoryDiscount,
    props.product?.ecoTax,
    props.product?.categoryDiscount?.rate,
    props.product?.id,
  ])

  const { isOwnBrand, isNew, isFastAndCheap, isDoNotPromoteAll } = determineSpecialFlags(props.product)
  return (
    <div
      className={cn(
        "absolute top-4 left-4 z-50 h-8 bg-transparent",
        {
          "top-2 left-2 h-8": props.variant === "landscape",
        },
        props.className
      )}
    >
      <div className={"flex flex-col items-start gap-0.5"}>
        {effectiveCardPrices && effectiveCardPrices.discountPercent > 0 ? (
          <CardDiscountBadge variant={props.variant} label={effectiveCardPrices.discountPercentFormatted} />
        ) : (
          <div></div>
        )}
        {SHOW_ADDITIONAL_BADGES && (
          <>
            {isNew && <CardDiscountBadge className={"bg-green-700"} variant={props.variant} label={t("product.new")} />}
            {isDoNotPromoteAll && (
              <CardDiscountBadge className={"bg-primary"} variant={props.variant} label={t("product.best_price")} />
            )}
            {isOwnBrand && (
              <CardDiscountBadge className={"bg-yellow-500"} variant={props.variant} label={t("product.own_brand")} />
            )}
            {isFastAndCheap && (
              <CardDiscountBadge className={"bg-red-600"} variant={props.variant} label={t("product.promotion")} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
